import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase/firebase';
import { userService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userPaymentData, setUserPaymentData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Function to refresh user payment data
  const refreshUserData = async () => {
    if (!currentUser) return;
    
    try {
      console.log('Refreshing user data for:', currentUser.uid);
      const userResult = await userService.getUserWithCache(currentUser.uid, true); // Force refresh
      
      if (userResult.success && userResult.user) {
        const userData = userResult.user;
        console.log('Fetched user data:', userData);
        console.log('Available fields in user data:', Object.keys(userData));
        console.log('Payment related fields:', {
          paymentStatus: userData.paymentStatus,
          expirationDate: userData.expirationDate,
          // Check for alternative field names
          payment_status: userData.payment_status,
          expiration_date: userData.expiration_date,
          paymentConfirmed: userData.paymentConfirmed,
          subscriptionEnd: userData.subscriptionEnd,
          premiumUntil: userData.premiumUntil,
          isPremium: userData.isPremium
        });
        
        // Check multiple possible field name variations
        const paymentStatus = userData.paymentStatus || userData.payment_status || userData.paymentConfirmed;
        const expirationDate = userData.expirationDate || userData.expiration_date || userData.subscriptionEnd || userData.premiumUntil;
        
        if (paymentStatus && expirationDate) {
          const paymentData = {
            paymentStatus: paymentStatus,
            expirationDate: expirationDate,
            paymentUpdatedAt: userData.paymentUpdatedAt
          };
          console.log('Setting payment data:', paymentData);
          setUserPaymentData(paymentData);
        } else {
          console.log('No payment data found for user. Payment status:', paymentStatus, 'Expiration date:', expirationDate);
          setUserPaymentData(null);
        }
      } else {
        console.log('Failed to fetch user data');
        setUserPaymentData(null);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
      setUserPaymentData(null);
    }
  };

  // Google Sign In
  const signInWithGoogle = async () => {
    setIsLoading(true);
    setAuthError(null);
    
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // Automatically create or update user in database
      const userResult = await userService.createOrUpdateUser(user);
      
      if (userResult.success) {
        // Log successful login
        await userService.logUserActivity(user.uid, user.email, 'login');
        
        if (userResult.isNewUser) {
          console.log('New user created successfully:', user.email);
        } else {
          console.log('Existing user updated successfully:', user.email);
        }
        
        return { success: true };
      } else {
        setAuthError('Failed to save user information. Please try again.');
        await signOut(auth);
        return { success: false, error: userResult.error };
      }
    } catch (error) {
      setAuthError(error.message);
      console.error('Authentication error:', error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Sign Out
  const logout = async () => {
    setIsLoading(true);
    try {
      if (currentUser) {
        await userService.logUserActivity(currentUser.uid, currentUser.email, 'logout');
      }
      await signOut(auth);
      setAuthError(null);
      setUserPaymentData(null); // Clear payment data on logout
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? user.uid : 'null');
      setCurrentUser(user);
      
      if (user) {
        // Update user information in database on auth state change
        await userService.createOrUpdateUser(user);
        
        // Fetch complete user data including payment information
        try {
          console.log('Fetching user data for:', user.uid);
          const userResult = await userService.getUserWithCache(user.uid);
          
          if (userResult.success && userResult.user) {
            const userData = userResult.user;
            console.log('Fetched user data:', userData);
            console.log('Available fields in user data:', Object.keys(userData));
            console.log('PaymentStatus field:', userData.paymentStatus);
            console.log('ExpirationDate field:', userData.expirationDate);
            console.log('Payment related fields:', {
              paymentStatus: userData.paymentStatus,
              expirationDate: userData.expirationDate,
              // Check for alternative field names
              payment_status: userData.payment_status,
              expiration_date: userData.expiration_date,
              paymentConfirmed: userData.paymentConfirmed,
              subscriptionEnd: userData.subscriptionEnd,
              premiumUntil: userData.premiumUntil,
              isPremium: userData.isPremium
            });
            
            // Check multiple possible field name variations
            const paymentStatus = userData.paymentStatus || userData.payment_status || userData.paymentConfirmed;
            const expirationDate = userData.expirationDate || userData.expiration_date || userData.subscriptionEnd || userData.premiumUntil;
            
            if (paymentStatus && expirationDate) {
              const paymentData = {
                paymentStatus: paymentStatus,
                expirationDate: expirationDate,
                paymentUpdatedAt: userData.paymentUpdatedAt
              };
              console.log('Setting payment data:', paymentData);
              setUserPaymentData(paymentData);
            } else {
              console.log('No payment data found for user. Payment status:', paymentStatus, 'Expiration date:', expirationDate);
              setUserPaymentData(null);
            }
          } else {
            console.log('Failed to fetch user data');
            setUserPaymentData(null);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUserPaymentData(null);
        }
      } else {
        setAuthError(null);
        setUserPaymentData(null);
      }
      
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value = {
    currentUser,
    userPaymentData,
    isLoading,
    authError,
    signInWithGoogle,
    logout,
    refreshUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};